import React, { FC, useState, useRef, useEffect, useCallback, useMemo } from 'react'
import Image from 'next/image'
import moment from 'moment'
import styles from './styles.module.css'

type Props = {
  label: string
  time: string
  minTime: string
  maxTime: string
  onSetTime: (time: string) => void
  disabled?: boolean
}

const times = Array.from({ length: 48 }, (_, i) => {
  const time = moment({ hour: Math.floor(i / 2), minute: (i % 2) * 30 }).format('HH:mm')
  return { label: time, value: time }
})

export const TimePicker: FC<Props> = ({ label, time, minTime, maxTime, onSetTime, disabled }) => {
  const ref = useRef<HTMLDivElement>()
  const timeListRef = useRef<HTMLDivElement>()

  const [showSelector, setShowSelector] = useState<boolean>(false)

  const [selectedTime, setSelectedTome] = useState<string>('')

  const timeOptions = useMemo(() => {
    if (minTime === maxTime) {
      return times
    }

    let minIndex = times.findIndex((item) => item.value === minTime)

    if (minIndex === -1) {
      minIndex = 0
    }

    let maxIndex = times.findIndex((item) => item.value === maxTime)

    if (maxIndex === -1) {
      maxIndex = times.length - 1
    }

    if (maxIndex < minIndex) {
      return times.filter((_, index) => index <= maxIndex || index >= minIndex)
    }

    return times.slice(minIndex, maxIndex + 1)
  }, [maxTime, minTime])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowSelector(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (showSelector) {
      const selectedIndex = timeOptions.findIndex((time) => time.value === selectedTime)
      timeListRef.current.scroll({
        top: 30 * selectedIndex,
        behavior: 'smooth',
      })
    }
  }, [selectedTime, showSelector, timeOptions])

  const handleSelectTime = useCallback(
    (time: string) => {
      setSelectedTome(time)
      onSetTime(time)
      setShowSelector(false)
    },
    [onSetTime]
  )

  return (
    <div className={styles.container}>
      <div className={styles.label}>{label}</div>
      <div ref={ref} className={styles.pickerContainer}>
        <div
          onClick={() => {
            if (disabled) return
            setShowSelector(true)
          }}
          className={`${styles.picker} ${disabled && styles.disabledPicker}`}
        >
          <div className={styles.pickedTime}>{time}</div>
          <Image src="/arrow-down.png" height={24} width={24} alt="" />
        </div>
        {showSelector && (
          <div className={styles.timeSelectorContainer}>
            <div ref={timeListRef} className={styles.list}>
              {timeOptions.map((timeOption, index) => {
                const isSelected = time === timeOption.value
                return (
                  <div
                    id={`time-${index}`}
                    key={index}
                    className={`${styles.item} ${isSelected && styles.itemSelected}`}
                    onClick={() => handleSelectTime(timeOption.value)}
                  >
                    <div className={styles.checkedContainer}>
                      {isSelected && <img src="/checked.png" alt="" height={18} width={18} />}
                    </div>
                    {timeOption.label}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
